import React, { useEffect, useState } from "react";
import { RichTreeView } from "@mui/x-tree-view";
import SearchIcon from "@mui/icons-material/Search";
import { Box, IconButton, TextField } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import ApiService from "../../../utils/fetch";
import {
  setCIManageForecastTreeData,
  setCIManageForecastBottlerData,
  setCIManageForecastFinalTreeData,
} from "../../../redux/CIManageScreenRedux";
import { useCIBottlerSelection } from "../../../context/CIBottlerSelectionContext";

const CIBottlerSelectionAccordion = () => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [treeData, setTreeData] = useState([]);
  const [filteredTreeData, SetFilteredTreeData] = useState([]);

  const dispatch = useDispatch();

  const ciManageForecastTreeDataRedux = useSelector(
    (state) => state.CIManageScreenRedux.ciManageForecastTreeData,
  );
  const { setSelectedData } = useCIBottlerSelection();

  //Event handler for when selected items change
  const handleSelectedItemsChange = (_event, ids) => {
    const newSelectedIds = determineIdsToSet(treeData, ids, selectedIds);

    setSelectedIds(newSelectedIds);

    const pcCountryIds = newSelectedIds.filter((id) =>
      id.startsWith("pcCountry"),
    );

    const ApplyTreeViewData = filterBottlersByPcCountry(pcCountryIds, treeData);

    const bottler = getSelectedBottler(newSelectedIds, treeData);

    dispatch(setCIManageForecastBottlerData(bottler));
    setSelectedData(ApplyTreeViewData);
  };

  const getSelectedBottler = (selectedIds, treeData) => {
    if (!selectedIds?.length) return null;

    const bottlerId = selectedIds.find((id) => id.startsWith("bottler"));
    if (bottlerId) {
      const node = treeData.find((b) => b.id === bottlerId);
      return node
        ? {
            bottler: node.bottler,
            bottlerOwnerCode: node.bottlerOwnerCode,
          }
        : null;
    }

    const pcCountryId = selectedIds.find((id) => id.startsWith("pcCountry"));
    if (!pcCountryId) return null;

    for (const node of treeData) {
      if (node.children?.some((c) => c.id === pcCountryId)) {
        return {
          bottler: node.bottler,
          bottlerOwnerCode: node.bottlerOwnerCode,
        };
      }
    }

    return null;
  };

  //Transform Data
  const transformData = (data) => {
    return data.map((bottlerItem, bottlerIndex) => ({
      id: `bottler${bottlerIndex + 1}`,
      // label: `${bottlerItem.bottler} - ${bottlerItem.bottlerOwnerCode}`,
      label: `${bottlerItem.bottler} - ${bottlerItem.bottlerOwnerCode}${
        bottlerItem.bottlerOwnerDescription
          ? ` - ${bottlerItem.bottlerOwnerDescription}`
          : ""
      }`,
      bottler: bottlerItem.bottler,
      bottlerOwnerCode: bottlerItem.bottlerOwnerCode,
      children: (bottlerItem.profitCenterDescription || []).map(
        (pcDesc, countryIndex) => ({
          id: `pcCountry${bottlerIndex + 1}-${countryIndex + 1}`,
          label: pcDesc,
          profitCenterCountry: pcDesc,
        }),
      ),
    }));
  };

  useEffect(() => {
    if (
      ciManageForecastTreeDataRedux === null ||
      ciManageForecastTreeDataRedux.length === 0
    ) {
      const getData = async () => {
        try {
          const response = await ApiService.get(
            "/v1/manage/ci/bottler-selection",
          );

          const bottler = response.data[0]?.bottler;
          dispatch(setCIManageForecastBottlerData(bottler));

          const transformedData = transformData(response.data);
          setTreeData(transformedData);
          SetFilteredTreeData(transformedData);

          const allIds = getAllIds(transformedData);
          dispatch(setCIManageForecastTreeData(transformedData));
          const filteredItems = allIds.filter((item) =>
            item.startsWith("pcCountry"),
          );
          const ApplyTreeViewData = filterBottlersByPcCountry(
            filteredItems,
            transformedData,
          );
          dispatch(setCIManageForecastFinalTreeData(ApplyTreeViewData));
        } catch (error) {
          console.error("Error posting data", error);
        }
      };
      getData();
    } else {
      setTreeData(ciManageForecastTreeDataRedux);
      SetFilteredTreeData(ciManageForecastTreeDataRedux);
    }
  }, []);

  //get All Ids from Tree Data
  const getAllIds = (items) => {
    const ids = [];
    items.forEach((item) => {
      ids.push(item.id);
      if (item.children) {
        ids.push(...getAllIds(item.children));
      }
    });
    return ids;
  };

  //Filter Bottlers By PcCountry Ids
  const filterBottlersByPcCountry = (pcCountryIds, transformedData) => {
    return transformedData.flatMap((bottler) =>
      bottler.children
        .filter((pc) => pcCountryIds.includes(pc.id))
        .map((pc) => ({
          profitCenterCountry: pc.profitCenterCountry,
          bottler: bottler.bottler,
          bottlerOwnerCode: bottler.bottlerOwnerCode,
        })),
    );
  };

  //Function to determine which IDs should be set based on the current and new selections
  function determineIdsToSet(fullTreeData, newIds, currentIds) {
    const isDeselectingNode = currentIds.length > newIds.length;

    if (isDeselectingNode) {
      const removed = currentIds.filter((id) => !newIds.includes(id))[0];
      const parentIdsToRemove = getAllParentIds(fullTreeData, removed);
      const childIdsToRemove = getSelectedIdsAndChildrenIds(fullTreeData, [
        removed,
      ]);

      return newIds.filter(
        (id) =>
          !parentIdsToRemove.includes(id) && !childIdsToRemove.includes(id),
      );
    }

    // Selecting new items
    const idsToSet = getSelectedIdsAndChildrenIds(fullTreeData, newIds);
    let parent = getParentNode(fullTreeData, newIds[newIds.length - 1]);

    while (parent) {
      const childIds = parent.children?.map((node) => node.id) ?? [];
      const allChildrenSelected = childIds.every((id) => idsToSet.includes(id));

      if (allChildrenSelected) {
        if (!idsToSet.includes(parent.id)) {
          idsToSet.push(parent.id);
        }
        parent = getParentNode(fullTreeData, parent.id);
      } else {
        break;
      }
    }
    return idsToSet;
  }

  // Helper function to get selected IDs along with their children
  function getSelectedIdsAndChildrenIds(items, selectedIds) {
    const selectedIdIncludingChildrenIds = new Set([...selectedIds]);
    for (const item of items) {
      if (selectedIds.includes(item.id)) {
        selectedIdIncludingChildrenIds.add(item.id);
        if (item.children) {
          const childrenIds = item.children.map((child) => child.id);
          const childrenSelectedIds = getSelectedIdsAndChildrenIds(
            item.children,
            childrenIds,
          );
          childrenSelectedIds.forEach((selectedId) =>
            selectedIdIncludingChildrenIds.add(selectedId),
          );
        }
      } else if (item.children) {
        const childrenSelectedIds = getSelectedIdsAndChildrenIds(
          item.children,
          selectedIds,
        );
        childrenSelectedIds.forEach((selectedId) =>
          selectedIdIncludingChildrenIds.add(selectedId),
        );
      }
    }
    return [...Array.from(selectedIdIncludingChildrenIds)];
  }

  // Helper function to find the parent node of a given item ID
  function getParentNode(items, id) {
    for (const item of items) {
      if (item.children) {
        if (item.children.some((child) => child.id === id)) {
          return item;
        } else {
          const parentNode = getParentNode(item.children, id);
          if (parentNode) {
            return parentNode;
          }
        }
      }
    }
    return undefined;
  }

  // Helper function to get all parent IDs of a given item ID
  function getAllParentIds(items, id) {
    const parentIds = [];
    let parent = getParentNode(items, id);
    while (parent) {
      parentIds.push(parent.id);
      parent = getParentNode(items, parent.id);
    }
    return parentIds;
  }

  //handle Search change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    SetFilteredTreeData(filterTreeData(treeData, event.target.value));
  };

  //Filter Tree Data
  const filterTreeData = (items, query) => {
    if (!query) {
      return items;
    }
    return items
      .filter((item) => {
        const matches = item.label.toLowerCase().includes(query.toLowerCase());
        const children = item.children
          ? filterTreeData(item.children, query)
          : [];
        return matches || children.length > 0;
      })
      .map((item) => {
        const matches = item.label.toLowerCase().includes(query.toLowerCase());
        const children = matches
          ? item.children
          : item.children
            ? filterTreeData(item.children, query)
            : [];
        return {
          ...item,
          children: children,
        };
      });
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          height: "75vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Search"
          margin="normal"
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{
            "& .MuiInputBase-input": {
              padding: "6px 8px",
              fontSize: "0.85rem",
            },
          }}
          InputProps={{
            endAdornment: (
              <IconButton size="small">
                <SearchIcon fontSize="small" />
              </IconButton>
            ),
          }}
        />
        <Box sx={{ flexGrow: 1, overflowY: "auto", p: 2, pt: 0 }}>
          <RichTreeView
            multiSelect
            checkboxSelection
            selectedItems={selectedIds}
            onSelectedItemsChange={handleSelectedItemsChange}
            items={filteredTreeData}
            sx={{
              flexGrow: 1,
              backgroundColor: "white",
              "& .MuiTreeItem-label": {
                fontSize: "11px",
              },
            }}
          />
        </Box>
      </Box>
    </React.Fragment>
  );
};

export default CIBottlerSelectionAccordion;
